import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Card, Tag, Image, Typography, Popconfirm, Badge } from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  ProfileOutlined,
  SlidersOutlined
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { PHOTO_URL } from '../../utils/apiutils'
import { usePermission } from '../../hooks/usePermission'

const { Meta } = Card
const { Text } = Typography

const StyledCard = styled(Card)`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f0f0f0;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
    border-color: #d5728a;
  }

  .ant-card-cover {
    height: 200px;
    overflow: hidden;
    position: relative;
    background: #f5f5f5;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${(props) =>
        props.isInactive ? 'rgba(0, 0, 0, 0.1)' : 'none'};
    }
  }

  .ant-card-actions {
    background: #fafafa;
    border-top: 1px solid #f0f0f0;
  }

  /* 手機版樣式調整 */
  @media screen and (max-width: 768px) {
    border-radius: 8px;

    .ant-card-cover {
      height: 140px;
    }

    .ant-card-meta-title {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ant-card-body {
      padding: 12px;
    }

    .ant-card-actions {
      padding: 8px 0;
    }
  }
`

const PriceTag = styled(Text)`
  color: #d5728a;
  font-size: 16px;
  font-weight: 600;

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const CostTag = styled(Text)`
  color: #666;
  font-size: 13px;
  margin-left: 8px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`

const StockTag = styled(Tag)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  border-radius: 12px;
  font-weight: 500;
  padding: 4px 12px;
`

const StatusTag = styled(Tag)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  border-radius: 12px;
  font-weight: 500;
  padding: 4px 12px;
`

const LowStockBadge = styled(Badge)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #666;

  @media screen and (max-width: 768px) {
    font-size: 11px;
  }
`

const InventoryProductCard = ({
  product,
  onDelete,
  onPurchase,
  onViewLedger,
  onAdjustStock
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = usePermission()
  // 只有具備 inventory:manage（或 *）才能進貨、編輯、刪除商品
  const canManageInventory =
    can('inventory:manage') || can('inventory:*') || can('*')
  // 具備 inventory:view（或 manage/*）即可查看進出庫異動明細
  const canViewInventory =
    can('inventory:view') ||
    can('inventory:manage') ||
    can('inventory:*') ||
    can('*')
  const DEFAULT_IMAGE_URL =
    'https://f005.backblazeb2.com/file/beautyapp/public/assets/samplebox.png'
  const PLACEHOLDER_IMAGE_URL =
    'https://via.placeholder.com/400x400?text=No+Image'
  const [imageError, setImageError] = useState(false)
  const [currentImageUrl, setCurrentImageUrl] = useState(null)

  const canViewFinance = can('finance:view')

  const {
    _id,
    id,
    name,
    title,
    manufacturer,
    capacity,
    suggestedPrice,
    price,
    cost,
    stock,
    lowStockThreshold = 10,
    unit = '件',
    status,
    images = [],
    categoryName,
    category,
    // 庫存以容量計價時後端會一併回傳解析結果，前端不再自己解析字串
    stockByCapacity,
    unitSize,
    sellableUnits
  } = product

  const productId = _id || id
  const productName = name || title
  const productPrice = suggestedPrice || price
  const displayCategoryName = categoryName || category?.name

  // 過濾掉默認圖片和占位符圖片，只保留真實上傳的圖片
  const realImages = images.filter((url) => {
    if (!url) return false
    const fullUrl = url.startsWith('http') ? url : `${PHOTO_URL}${url}`
    return fullUrl !== DEFAULT_IMAGE_URL && fullUrl !== PLACEHOLDER_IMAGE_URL
  })

  // 初始化圖片URL
  useEffect(() => {
    if (realImages.length > 0 && !imageError) {
      const imageUrl = realImages[0].startsWith('http')
        ? realImages[0]
        : `${PHOTO_URL}${realImages[0]}`
      setCurrentImageUrl(imageUrl)
    } else {
      setCurrentImageUrl(DEFAULT_IMAGE_URL)
    }
  }, [realImages, imageError])

  // 以容量計價的商品：湊不滿一罐就等同缺貨（客人買不到）
  const byCapacity = !!stockByCapacity && Number(unitSize) > 1
  const units = Number(sellableUnits) || 0
  const isLowStock = stock <= lowStockThreshold
  const isOutOfStock = byCapacity ? units < 1 : stock === 0

  // 處理圖片加載錯誤
  const handleImageError = () => {
    if (currentImageUrl !== DEFAULT_IMAGE_URL) {
      setImageError(true)
      setCurrentImageUrl(DEFAULT_IMAGE_URL)
    }
  }

  const handleEdit = () => {
    // 帶上目前列表的網址（含分類／搜尋），編輯頁的返回鍵在沒有站內歷史時
    //（重整、直接貼網址）才回得到原本的列表狀態
    navigate(`/inventory/products/edit/${productId}`, {
      state: { from: `${location.pathname}${location.search}` }
    })
  }

  // 點擊「進貨」快捷按鈕：
  // 1. 若父層提供 onPurchase（例如商品管理頁的 Modal 模式）→ 呼叫之，由父層開 Modal
  // 2. 否則 fallback 跳轉到進貨管理頁，並透過 location.state 帶入預選商品
  const handleQuickPurchase = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
    if (typeof onPurchase === 'function') {
      onPurchase(productId, product)
      return
    }
    navigate('/inventory/purchase', {
      state: { preselectedProductId: productId }
    })
  }

  // 點擊「異動明細」：開啟單品進出庫流水 Modal（由父層控制）
  const handleViewLedger = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
    if (typeof onViewLedger === 'function') {
      onViewLedger(productId, product)
    }
  }

  // 點擊「調整庫存」：開啟專用調整 Modal（由父層控制）
  const handleAdjustStock = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation()
    }
    if (typeof onAdjustStock === 'function') {
      onAdjustStock(productId, product)
    }
  }

  const cardActions = []

  // 具備 inventory:view 即可查看進出庫異動明細（數量人人可看，金額由後端依 finance:view 控管）
  if (canViewInventory && typeof onViewLedger === 'function') {
    cardActions.push(
      <ProfileOutlined
        key='ledger'
        onClick={handleViewLedger}
        style={{ fontSize: '18px', color: '#1677ff' }}
        title='異動明細'
      />
    )
  }

  // 具備 inventory:manage 才能調整庫存（走專用流程，會寫入異動明細）
  if (canManageInventory && typeof onAdjustStock === 'function') {
    cardActions.push(
      <SlidersOutlined
        key='adjust'
        onClick={handleAdjustStock}
        style={{ fontSize: '18px', color: '#13c2c2' }}
        title='調整庫存'
      />
    )
  }

  // 只有有 finance:view 權限且具備 inventory:manage 才能進行進貨（因為進貨頁面涉及成本與付款資訊）
  if (canViewFinance && canManageInventory) {
    cardActions.push(
      <ShoppingCartOutlined
        key='purchase'
        onClick={handleQuickPurchase}
        style={{ fontSize: '18px', color: '#d5728a' }}
        title='進貨'
      />
    )
  }

  // 只有有 finance:view 權限且具備 inventory:manage 才能編輯（因為編輯頁面會顯示成本）
  if (canViewFinance && canManageInventory) {
    cardActions.push(
      <EditOutlined
        key='edit'
        onClick={handleEdit}
        style={{ fontSize: '18px' }}
      />
    )
  }

  // 只有具備 inventory:manage 才能刪除
  if (canManageInventory) {
    cardActions.push(
      <Popconfirm
        title='確定要刪除此商品嗎？'
        description='刪除後無法恢復'
        onConfirm={() => onDelete(productId)}
        okText='確定'
        cancelText='取消'
        key='delete'
      >
        <DeleteOutlined style={{ fontSize: '18px', color: '#ff4d4f' }} />
      </Popconfirm>
    )
  }

  // 如果商品已停用，添加圖標提示
  if (status === 'inactive') {
    cardActions.unshift(
      <EyeInvisibleOutlined
        key='inactive'
        style={{ color: '#999', fontSize: '18px' }}
        title='商品已停用'
      />
    )
  }

  return (
    <StyledCard
      isInactive={status === 'inactive'}
      cover={
        <div style={{ position: 'relative', height: '100%' }}>
          <img
            alt={productName}
            src={currentImageUrl || DEFAULT_IMAGE_URL}
            onError={handleImageError}
            style={{
              objectFit: 'cover',
              height: '100%',
              width: '100%',
              filter: status === 'inactive' ? 'grayscale(50%)' : 'none',
              display: 'block'
            }}
          />
          <StatusTag color={status === 'active' ? 'success' : 'default'}>
            {status === 'active' ? '啟用' : '停用'}
          </StatusTag>
          {isLowStock && !isOutOfStock && (
            <StockTag color='warning'>低庫存</StockTag>
          )}
          {isOutOfStock && <StockTag color='error'>缺貨</StockTag>}
          {!isLowStock && !isOutOfStock && (
            <StockTag color='success'>
              庫存 {stock}
              {byCapacity && ` · 可售 ${units} 罐`}
            </StockTag>
          )}
        </div>
      }
      actions={cardActions}
    >
      <Meta
        title={
          <div
            style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}
          >
            {productName}
          </div>
        }
        description={
          <ProductInfo>
            {displayCategoryName && (
              <InfoRow>
                <span>分類</span>
                <span>{displayCategoryName}</span>
              </InfoRow>
            )}
            {manufacturer && (
              <InfoRow>
                <span>廠商</span>
                <span>{manufacturer}</span>
              </InfoRow>
            )}
            {capacity && (
              <InfoRow>
                <span>容量</span>
                <span>{capacity}</span>
              </InfoRow>
            )}
            <InfoRow
              style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid #f0f0f0'
              }}
            >
              <span>建議售價</span>
              <PriceTag>NT$ {productPrice || 0}</PriceTag>
            </InfoRow>
            {canViewFinance && cost !== undefined && cost !== null && (
              <InfoRow>
                <span>成本</span>
                <CostTag>NT$ {cost || 0}</CostTag>
              </InfoRow>
            )}
            <InfoRow style={{ marginTop: '4px' }}>
              <span>庫存數量</span>
              <Text
                strong
                style={{
                  color: isOutOfStock
                    ? '#ff4d4f'
                    : isLowStock
                    ? '#faad14'
                    : '#52c41a',
                  fontSize: '14px'
                }}
              >
                {stock} {unit}
                {byCapacity && (
                  <span style={{ color: '#8c8c8c', fontWeight: 400 }}>
                    {`（一罐 ${unitSize}，可售 ${units} 罐）`}
                  </span>
                )}
              </Text>
            </InfoRow>
          </ProductInfo>
        }
      />
    </StyledCard>
  )
}

export default InventoryProductCard
